import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusChip } from "@/components/common/StatusChip";
import { useAuth } from "@/auth/useAuth";
import { downloadStudentsXlsx, fetchStudents } from "@/api/students";
import { fetchSubjects } from "@/api/branches";
import { fetchGroups } from "@/api/groups";
import { fetchTeachers } from "@/api/teachers";
import { LEAD_SOURCE_LABELS, STUDENT_STATUS_LABELS } from "@/types/enums";
import { formatMoney, formatPercent } from "@/utils/format";
import type { Student } from "@/types/student";
import { StudentCreateDialog } from "./StudentCreateDialog";
import { StudentsImportDialog } from "./StudentsImportDialog";
import { useTranslation } from "react-i18next";

export function StudentsListPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasPermission, user } = useAuth();
  const canEdit = hasPermission("students.edit");
  const canSeePhone = hasPermission("students.phone");
  const canSeeFinance = hasPermission("fin.debts");
  const canExport = ["DIRECTOR", "ACCOUNTANT"].includes(user?.role ?? "");

  const [search, setSearch] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [teacherId, setTeacherId] = useState("");
  const [source, setSource] = useState("");
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const { data: subjects } = useQuery({ queryKey: ["subjects"], queryFn: fetchSubjects });
  const { data: teachers } = useQuery({ queryKey: ["teachers", "all"], queryFn: () => fetchTeachers({ limit: 200 }) });
  const { data: groups } = useQuery({ queryKey: ["groups", "all"], queryFn: () => fetchGroups({ limit: 200 }) });

  const query = {
    page: paginationModel.page + 1,
    limit: paginationModel.pageSize,
    search: search || undefined,
    subjectId: subjectId || undefined,
    groupId: groupId || undefined,
    teacherId: teacherId || undefined,
    source: source || undefined,
  };

  const { data, isLoading } = useQuery({
    queryKey: ["students", query],
    queryFn: () => fetchStudents(query),
  });

  const columns = useMemo<GridColDef<Student>[]>(() => {
    const cols: GridColDef<Student>[] = [
      { field: "fullName", headerName: "ФИО", flex: 1.2, minWidth: 180 },
    ];
    if (canSeePhone) {
      cols.push({ field: "phone", headerName: "Телефон", width: 160 });
    }
    cols.push(
      {
        field: "groupNames",
        headerName: "Группы",
        flex: 1,
        minWidth: 140,
        valueGetter: (_, row) => (row.groupNames ?? []).join(", "),
      },
      {
        field: "subjectNames",
        headerName: "Предметы",
        flex: 1,
        minWidth: 140,
        valueGetter: (_, row) => (row.subjectNames ?? []).join(", "),
      },
      {
        field: "attendancePercent",
        headerName: "Посещаемость",
        width: 130,
        valueGetter: (_, row) => formatPercent(row.attendancePercent),
      },
      {
        field: "averageGrade",
        headerName: "Средний балл",
        width: 120,
        valueGetter: (_, row) => row.averageGrade ?? "—",
      },
      {
        field: "status",
        headerName: "Статус",
        width: 130,
        renderCell: (params) => <StatusChip label={STUDENT_STATUS_LABELS[params.row.status]} />,
      },
    );
    if (canSeeFinance) cols.push({ field: "debt", headerName: "Долг", width: 130, valueGetter: (_, row) => formatMoney(row.debt ?? 0) });
    return cols;
  }, [canSeePhone, canSeeFinance]);

  return (
    <>
      <PageHeader
        title={t("students.title")}
        subtitle={t("students.subtitle")}
        actions={
          <>
            {canExport && (
              <>
                <Button variant="outlined" onClick={() => setImportOpen(true)}>{t("common.import")}</Button>
                <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => void downloadStudentsXlsx(query)}>
                  {t("common.downloadXlsx")}
                </Button>
              </>
            )}
            {canEdit && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
                {t("students.add")}
              </Button>
            )}
          </>
        }
      />

      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} flexWrap="wrap">
          <TextField
            label={t("students.search")}
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 220 }}
          />
          <TextField
            select
            label={t("students.subject")}
            size="small"
            value={subjectId}
            onChange={(e) => setSubjectId(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">{t("students.allSubjects")}</MenuItem>
            {(subjects ?? []).map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t("students.group")}
            size="small"
            value={groupId}
            onChange={(e) => setGroupId(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">{t("students.allGroups")}</MenuItem>
            {(groups?.items ?? []).map((g) => (
              <MenuItem key={g.id} value={g.id}>
                {g.code}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t("students.teacher")}
            size="small"
            value={teacherId}
            onChange={(e) => setTeacherId(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">{t("students.allTeachers")}</MenuItem>
            {(teachers?.items ?? []).map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.fullName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t("students.source")}
            size="small"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="">{t("students.allSources")}</MenuItem>
            {Object.entries(LEAD_SOURCE_LABELS).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ height: 620, overflow: "hidden" }}>
        <DataGrid
          rows={data?.items ?? []}
          rowCount={data?.total ?? 0}
          columns={columns}
          loading={isLoading}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          onRowClick={(params) => navigate(`/students/${params.id}`)}
          sx={{
            border: "none",
            cursor: "pointer",
            "& .MuiDataGrid-columnHeaders": { borderBottomColor: "var(--border)" },
            "& .MuiDataGrid-columnHeaderTitle": { color: "var(--text-3)", fontSize: 11, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase" },
            "& .MuiDataGrid-cell": { borderBottomColor: "var(--border)" },
            "& .MuiDataGrid-row:hover": { backgroundColor: "var(--surface-2)" },
          }}
        />
      </Paper>

      {canEdit && <StudentCreateDialog open={createOpen} onClose={() => setCreateOpen(false)} />}
      {canExport && <StudentsImportDialog open={importOpen} onClose={() => setImportOpen(false)} />}
    </>
  );
}
